/** Shared row-keyboard for board-table and board-list. Table uses AG cell focus only as a row cursor (↑↓, Space to select). List paints a row highlight and never focuses a cell. */

export interface BoardGridRow {
  group?: boolean;
  selectable?: boolean;
  rowIndex?: number | null;
  expanded?: boolean;
  /** Tree depth. Root rows are 0; AG's synthetic root node is -1. */
  level?: number;
  /** The owning branch node, so ArrowLeft on a leaf can walk out to it. */
  parent?: BoardGridRow | null;
  data?: { _id?: unknown };
  isSelected?(): boolean;
  setSelected?(selected: boolean): void;
  setExpanded?(expanded: boolean): void;
  /** SSRM tree rows report children here (`isServerSideGroup`), never through `group`. */
  hasChildren?(): boolean;
}

export interface BoardGridApi {
  getDisplayedRowCount(): number;
  getDisplayedRowAtIndex(index: number): BoardGridRow | null | undefined;
  getFocusedCell?(): { rowIndex?: number | null } | null | undefined;
  getRowNode?(id: string): BoardGridRow | null | undefined;
}

export interface BoardGridNavigateParams<TPosition extends { rowIndex: number }> {
  key?: string;
  event?: { key?: string };
  api?: BoardGridApi;
  previousCellPosition?: TPosition | null;
  nextCellPosition?: TPosition | null;
}

const TYPING_FOCUS = 'input, textarea, select, [contenteditable="true"]';

/** Group rows are chrome unless the mount sets `selectable.includeGroups`. Tree parents opt in. */
export function isBoardGridRowSelectable(node: BoardGridRow | null | undefined, includeGroups = false): boolean {

  if (!node) return false;
  return includeGroups || node.group !== true;
}

/** Table: cell focus is the row cursor when selecting or grouping. List always suppresses cells. */
export function boardGridSuppressCellFocus(args: { selectable: boolean; grouping: boolean }): boolean {

  return !args.selectable && !args.grouping;
}

export function boardGridHasRows(api: BoardGridApi | null | undefined): boolean {

  return !!api && api.getDisplayedRowCount() > 0;
}

export type BoardGridRowKey = 'up' | 'down' | 'toggle' | 'open' | 'expand' | 'collapse';

export function boardGridRowKey(key: string): BoardGridRowKey | null {

  if (key === 'ArrowDown') return 'down';
  if (key === 'ArrowUp') return 'up';
  if (key === 'ArrowRight') return 'expand';
  if (key === 'ArrowLeft') return 'collapse';
  if (key === 'Enter') return 'open';
  if (key === ' ') return 'toggle';
  return null;
}

/** Next displayed row in `direction`. `from` null starts at the edge (down → first, up → last). */
export function boardGridStepRow(api: BoardGridApi, from: number | null, direction: 1 | -1): number | null {

  if (!boardGridHasRows(api)) return null;
  const last = api.getDisplayedRowCount() - 1;
  let rowIndex = from == null ? (direction > 0 ? -1 : last + 1) : from;
  rowIndex += direction;
  while (rowIndex >= 0 && rowIndex <= last) {
    if (api.getDisplayedRowAtIndex(rowIndex)) return rowIndex;
    rowIndex += direction;
  }
  return from;
}

/**
 * AG `navigateToNextCell`: same cell position, different row. Spread keeps `column` as AG `Column`.
 * Left/right stay put — boards move by row, not by column.
 */
export function boardGridNavigateToNextRow<TPosition extends { rowIndex: number }>(params: BoardGridNavigateParams<TPosition>): TPosition | null | undefined {

  const key = params.key ?? params.event?.key;
  const previous = params.previousCellPosition;
  if (key === 'ArrowLeft' || key === 'ArrowRight') return previous ?? undefined;
  if (key !== 'ArrowUp' && key !== 'ArrowDown') return params.nextCellPosition ?? undefined;
  const api = params.api;
  if (!api || !previous) return params.nextCellPosition ?? undefined;
  const rowIndex = boardGridStepRow(api, previous.rowIndex, key === 'ArrowDown' ? 1 : -1);
  if (rowIndex == null) return previous;
  return { ...previous, rowIndex };
}

// The cursor is a row index, so when the panel moves to another record (create, reveal) it would keep painting the old row beside the panel's (WBS-12122).
export function boardGridCursorAfterPanelMove(api: BoardGridApi, cursor: number | null, panelRowId: string | null): number | null {

  if (cursor == null || panelRowId == null) return cursor;
  const cursorRowId = api.getDisplayedRowAtIndex(cursor)?.data?._id;
  return cursorRowId != null && String(cursorRowId) === panelRowId ? cursor : null;
}

/** When the side panel is already open, keyboard moves that same row — it does not paint a second cursor. */
export function boardGridOpenPanelRowId(panelOpen: boolean, rowId: string | null | undefined): string | null {

  if (!panelOpen || rowId == null || rowId === '') return null;
  return String(rowId);
}

/**
 * Click or Enter on a row shows it in the editor; a different row retargets it. Clicking the row that is
 * already open is a no-op rather than a toggle: "show this" must be idempotent, and dismissing the editor
 * is the panel's own close control. A click that carries no id keeps whatever is open.
 */
export function boardGridClickFocusedRowId(currentId: string | null | undefined, clickedId: string | null | undefined): string | null {

  if (clickedId == null || clickedId === '') return currentId ?? null;
  return clickedId;
}

export function boardGridToggleGroup(node: BoardGridRow | null | undefined): boolean {

  if (!boardGridRowHasChildren(node) || !node?.setExpanded) return false;
  node.setExpanded(!node.expanded);
  return true;
}

/**
 * Space on a row cursor. Selection wins wherever the mount allows the row to be selected — a board
 * that offers bulk actions must be drivable from the keyboard alone, and the tree's own open/close
 * is what ArrowLeft/ArrowRight are for. Group toggle is the fallback for rows selection excludes
 * (grouping chrome on a non-`includeGroups` mount), which keeps the pre-tree behaviour intact.
 */
export function boardGridToggleRow(node: BoardGridRow | null | undefined, includeGroups = false): boolean {

  if (!node) return false;
  if (node.setSelected && isBoardGridRowSelectable(node, includeGroups) && node.selectable !== false) {
    node.setSelected(node.isSelected?.() !== true);
    return true;
  }
  return boardGridToggleGroup(node);
}

/**
 * Whether the row has children to open. Client-side grouping marks parents with `group`; the
 * server-side tree (the outline) never sets `group` and answers through `hasChildren()` instead, so
 * both are read — a Service Line looked like a leaf to the arrow keys when only `group` was checked.
 */
export function boardGridRowHasChildren(node: BoardGridRow | null | undefined): boolean {

  return !!node && (node.group === true || node.hasChildren?.() === true);
}

/** Whether this row can open — a tree parent, or a grouping row. */
export function isBoardGridExpandableRow(node: BoardGridRow | null | undefined): boolean {

  return boardGridRowHasChildren(node) && typeof node?.setExpanded === 'function';
}

/**
 * What ArrowRight / ArrowLeft do to the row cursor, as a decision the caller then performs.
 *
 * The two keys are the tree's own axis and read as one rule in each direction: right opens, and once
 * open steps in; left closes, and once closed steps out. That means a held ArrowRight walks down into
 * the deepest branch and a held ArrowLeft walks back out to the root, which is the behaviour every
 * tree widget has and the reason neither key is a plain toggle.
 *
 * `null` is a deliberate no-op — a leaf has nothing to open, and a root row has nowhere to step out
 * to. The caller must NOT consume the key in that case, so the board's own left/right (nothing today,
 * horizontal scroll on a wide table tomorrow) stays reachable.
 */
export type BoardGridTreeAction = { kind: 'expand' | 'collapse'; node: BoardGridRow } | { kind: 'move'; rowIndex: number };

export function boardGridTreeAction(api: BoardGridApi | null | undefined, rowIndex: number | null, direction: 'expand' | 'collapse'): BoardGridTreeAction | null {

  if (!api || rowIndex == null) return null;
  const node = api.getDisplayedRowAtIndex(rowIndex);
  if (!node) return null;

  if (direction === 'expand') {
    if (!isBoardGridExpandableRow(node)) return null;
    if (node.expanded !== true) return { kind: 'expand', node };
    const child = boardGridFirstChildRowIndex(api, rowIndex);
    return child == null ? null : { kind: 'move', rowIndex: child };
  }

  if (isBoardGridExpandableRow(node) && node.expanded === true) return { kind: 'collapse', node };
  const parent = boardGridParentRowIndex(api, rowIndex);
  return parent == null ? null : { kind: 'move', rowIndex: parent };
}

/**
 * The row a key should act on when the painted cursor is missing.
 *
 * The cursor is our own painted class, and plenty of things move the user without touching it —
 * AG's own cell focus, and the side panel opening on a row the board never gave a cursor. Rather
 * than dropping the key (which is what made ArrowLeft/ArrowRight dead after a click), fall back in
 * order of how directly each source says "the user is here":
 *
 *   1. the painted cursor, when we have one — it is the thing the user can see;
 *   2. AG's focused cell, which a click or AG's own navigation leaves on the clicked row;
 *   3. the row the panel is showing, resolved back to a displayed index by id.
 *
 * Returns null when none of the three answer, and the caller must then leave the key alone.
 */
export function boardGridResolveCursorRow(api: BoardGridApi | null | undefined, cursor: number | null, focusedRowId: string | null | undefined, preferFocusedRowId = false): number | null {

  if (!api) return null;
  // A reveal or tree expansion can move the panel's row while the old numeric cursor still exists.
  if (preferFocusedRowId) {
    const focusedRow = boardGridRowIndexById(api, focusedRowId);
    if (focusedRow != null) return focusedRow;
  }
  if (cursor != null && api.getDisplayedRowAtIndex(cursor)) return cursor;

  const focusedCell = api.getFocusedCell?.()?.rowIndex;
  if (focusedCell != null && focusedCell >= 0 && api.getDisplayedRowAtIndex(focusedCell)) return focusedCell;

  return boardGridRowIndexById(api, focusedRowId);
}

/**
 * The displayed index of a row id. Prefers AG's own id lookup — an SSRM tree only keeps loaded
 * blocks, so scanning displayed rows is the fallback, not the primary answer.
 */
export function boardGridRowIndexById(api: BoardGridApi | null | undefined, rowId: string | null | undefined): number | null {

  if (!api || rowId == null || rowId === '') return null;

  const node = api.getRowNode?.(rowId);
  if (node?.rowIndex != null && node.rowIndex >= 0) return node.rowIndex;

  const rowCount = api.getDisplayedRowCount();
  for (let index = 0; index < rowCount; index++) {
    const row = api.getDisplayedRowAtIndex(index);
    if (row?.data?._id != null && String(row.data._id) === rowId) return index;
  }
  return null;
}

/** The row directly under an open branch, only when it really is that branch's child. */
export function boardGridFirstChildRowIndex(api: BoardGridApi, rowIndex: number): number | null {

  const next = api.getDisplayedRowAtIndex(rowIndex + 1);
  if (!next) return null;
  const parent = api.getDisplayedRowAtIndex(rowIndex);
  if (next.parent && parent && next.parent === parent) return rowIndex + 1;
  // Lazy trees can hand back a loading placeholder with no `parent` wired yet; depth still separates them.
  return depthOf(next) > depthOf(parent) ? rowIndex + 1 : null;
}

/**
 * The branch this row hangs under. Walks up by displayed index rather than trusting `node.parent`
 * to carry a `rowIndex`: an SSRM parent node is a model node, and only the displayed row it maps to
 * is a cursor position.
 */
export function boardGridParentRowIndex(api: BoardGridApi, rowIndex: number): number | null {

  const node = api.getDisplayedRowAtIndex(rowIndex);
  if (!node) return null;
  const parent = node.parent;
  const depth = depthOf(node);
  for (let index = rowIndex - 1; index >= 0; index--) {
    const candidate = api.getDisplayedRowAtIndex(index);
    if (!candidate) continue;
    if (parent && candidate === parent) return index;
    if (!parent && depthOf(candidate) < depth) return index;
  }
  return null;
}

function depthOf(node: BoardGridRow | null | undefined): number {

  return node?.level ?? 0;
}

/**
 * Whether this board should place a row cursor at all.
 *
 * A cursor is worth placing wherever a row key does something. Space needs selection; Enter needs
 * only a panel. Asking for selection alone is what left a read-only board (a Started Service Plan,
 * whose catalogue lock strips selection and bulk) with no cursor — and with no cursor
 * {@link boardGridShouldHandleKey} falls back to asking where DOM focus is, which inside an inspector
 * is chrome outside the grid, so every row key died.
 */
export function boardGridWantsRowCursor(args: { selectable: boolean; hasPanel: boolean }): boolean {

  return args.selectable || args.hasPanel;
}

export function boardGridFirstFocusableRowIndex(api: BoardGridApi, includeGroups = false): number | null {

  const rowCount = api.getDisplayedRowCount();
  for (let index = 0; index < rowCount; index++) {
    const row = api.getDisplayedRowAtIndex(index);
    if (!row || row.selectable === false || !isBoardGridRowSelectable(row, includeGroups)) continue;
    return row.rowIndex ?? index;
  }
  return null;
}

export function boardGridIsTypingTarget(activeElement: EventTarget | null): boolean {

  return activeElement instanceof HTMLElement && !!activeElement.closest(TYPING_FOCUS);
}

export function boardGridCanEnterFromKeyboard(activeElement: EventTarget | null, host: HTMLElement, root: Document, allowInsideHost = false): boolean {

  if (boardGridIsTypingTarget(activeElement)) return false;
  if (!activeElement || activeElement === root.body || activeElement === root.documentElement) return true;
  if (!(activeElement instanceof HTMLElement)) return false;
  if (host === activeElement) return true;
  return allowInsideHost && host.contains(activeElement);
}

/**
 * List/outline keys while the list is the composite (row = cursor, panel follows).
 * Focus inside `board-panel` is a different widget — date/select/text keep their keys.
 */
export function boardGridShouldHandleKey(activeElement: EventTarget | null, host: HTMLElement, root: Document, hasRowFocus: boolean): boolean {

  if (activeElement instanceof HTMLElement && activeElement.closest('board-panel')) return false;
  if (boardGridIsTypingTarget(activeElement)) return false;
  if (hasRowFocus) return true;
  return boardGridCanEnterFromKeyboard(activeElement, host, root, true);
}

/** Paints AG's row-focus classes without focusing a cell. Full-width groups also get `group-row-focused`. */
export function boardGridApplyRowFocus(host: HTMLElement, api: BoardGridApi, previous: number | null, next: number | null): void {

  paint(previous, false);
  paint(next, true);

  function paint(index: number | null, on: boolean): void {

    if (index == null) return;
    const group = api.getDisplayedRowAtIndex(index)?.group === true;
    host.querySelectorAll(`.ag-row[row-index="${index}"], .ag-full-width-row[row-index="${index}"]`).forEach(row => {
      row.classList.toggle('ag-row-focus', on);
      if (group) row.classList.toggle('group-row-focused', on);
    });
  }
}
